import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { Info, ShieldCheck } from 'lucide-react';
import EntraMigrationDialog from './EntraMigrationDialog';
import { useTwoStepVerification } from '../hooks/useTwoStepVerification';

// The account's security level on the profile page (docs/EntraFullMigrationPlan.md
// D20): bronze = the legacy login, silver = Microsoft sign-in, gold = Microsoft
// with two-step verification. The level comes from the server (securityLevel).
// "Upgrade" leads to the next level, so nobody waits for the migration popup.
const LEVELS = {
  bronze: { label: 'ארד', color: '#B0723A' },
  silver: { label: 'כסף', color: '#8A9099' },
  gold: { label: 'זהב', color: '#C9A227' },
};

const EXPLANATION = (
  <Box dir="rtl" sx={{ p: 0.5 }}>
    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>רמת האבטחה של החשבון</Typography>
    <Typography variant="body2"><b>ארד:</b> הכניסה הישנה של משלום, שמוחלפת בקרוב.</Typography>
    <Typography variant="body2"><b>כסף:</b> הכניסה המאובטחת החדשה. סיסמה לפי כללי האבטחה של Microsoft, ואיפוס סיסמה בקוד למייל.</Typography>
    <Typography variant="body2"><b>זהב:</b> כניסה מאובטחת עם אימות דו-שלבי: גם אם הסיסמה נחשפה, אי אפשר להיכנס בלי הקוד.</Typography>
  </Box>
);

const SecurityShield = ({ level, canUpgradeToSilver = false, canUpgradeToGold = false }) => {
  const [migrationOpen, setMigrationOpen] = useState(false);
  // Silver to gold is the same process as the "אימות דו-שלבי" switch.
  const { change: changeTwoStepVerification, isSaving, error } = useTwoStepVerification();
  const current = LEVELS[level];
  if (!current) return null;

  const canUpgrade = (level === 'bronze' && canUpgradeToSilver) || (level === 'silver' && canUpgradeToGold);
  const upgrade = () => {
    if (level === 'bronze') setMigrationOpen(true);
    else changeTwoStepVerification(true);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} dir="rtl">
      <ShieldCheck size={22} color={current.color} fill={current.color} fillOpacity={0.15} aria-hidden="true" />
      <Typography variant="body2" sx={{ color: '#103C6E', fontWeight: 'bold' }}>
        רמת אבטחה: {current.label}
      </Typography>
      <Tooltip title={EXPLANATION} arrow enterTouchDelay={0} leaveTouchDelay={5000}>
        <Box component="span" sx={{ display: 'inline-flex', cursor: 'help' }} aria-label="הסבר על רמות האבטחה" tabIndex={0}>
          <Info size={16} color="#667085" />
        </Box>
      </Tooltip>
      {canUpgrade && (
        <Button size="small" variant="outlined" onClick={upgrade} disabled={isSaving} sx={{ mr: 1, borderRadius: '8px' }}>
          {isSaving ? 'מעדכן...' : 'שדרוג'}
        </Button>
      )}
      {error && (
        <Typography color="error" role="alert" variant="body2">{error}</Typography>
      )}
      {migrationOpen && (
        // Opened by the user: closing it is not a "later" of the migration popup.
        <EntraMigrationDialog open onLater={() => setMigrationOpen(false)} />
      )}
    </Box>
  );
};

SecurityShield.propTypes = {
  level: PropTypes.oneOf(['bronze', 'silver', 'gold']),
  canUpgradeToSilver: PropTypes.bool,
  canUpgradeToGold: PropTypes.bool,
};

export default SecurityShield;
